import React, { useEffect, useState } from 'react';
import { Table, Popconfirm, message, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInquiries, deleteInquiry } from '../../features/getInTouchSlice';
import App from '../../Utils/LayoutMobile';
import dayjs from 'dayjs';

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const TrashIcon   = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
const MailIcon    = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const PhoneIcon   = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const UserIcon    = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const CalendarIcon= () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const ReplyIcon   = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>;
const GmailIcon   = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
    <path d="M6 8h36a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z" fill="#fff" stroke="#e0e0e0" strokeWidth="1.5"/>
    <path d="M4 10l20 14L44 10" stroke="#EA4335" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);
const OutlookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
    <rect x="4" y="8" width="40" height="32" rx="3" fill="#0078D4" opacity="0.12" stroke="#0078D4" strokeWidth="1.5"/>
    <path d="M4 16h40M16 16v24" stroke="#0078D4" strokeWidth="1.5"/>
    <rect x="19" y="20" width="20" height="14" rx="2" fill="#0078D4" opacity="0.2"/>
  </svg>
);
const ExternalIcon= () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;

// ── Section Label ──
function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

// ── Reply Modal Component ──
function ReplyModal({ visible, record, onClose }) {
  if (!record) return null;
  const email = record.email || "";

  const handleOption = (type) => {
    if (!email) { message.warning("No email found for this enquiry"); return; }

    const subject = encodeURIComponent(`Re: Enquiry from ${record.name || "Delegate"}`);
    const body    = encodeURIComponent(
      `Dear ${record.name || ""},\n\nThank you for reaching out to us.\n\nRegards,\nConference Team`
    );

    if (type === "gmail") {
      window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${subject}&body=${body}`, "_blank");
    } else if (type === "outlook") {
      window.open(`https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(email)}&subject=${subject}&body=${body}`, "_blank");
    } else {
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    }
    onClose();
  };

  // Backdrop click closes modal
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position:"fixed", inset:0,
          background:"rgba(26,0,64,0.35)",
          backdropFilter:"blur(4px)",
          zIndex:1000,
          animation:"fadeIn 0.18s ease",
        }}
      />

      {/* Modal Box */}
      <div style={{
        position:"fixed",
        top:"50%", left:"50%",
        transform:"translate(-50%,-50%)",
        zIndex:1001,
        background:"#fff",
        borderRadius:24,
        padding:"32px 28px 24px",
        width:340,
        boxShadow:"0 24px 64px rgba(26,0,64,0.18)",
        animation:"slideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        {/* Reply icon */}
        <div style={{
          width:52, height:52, borderRadius:"50%",
          background:"linear-gradient(135deg, rgba(240,77,160,0.12), rgba(139,47,201,0.12))",
          display:"flex", alignItems:"center", justifyContent:"center",
          margin:"0 auto 16px",
          color:C_PURPLE,
          border:"1.5px solid rgba(139,47,201,0.15)",
        }}>
          <ReplyIcon />
        </div>

        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:13, color:C_TEXT_MUTED, marginBottom:6 }}>Reply to</div>
          <div style={{
            fontSize:15, fontWeight:700, color:C_PURPLE,
            wordBreak:"break-all", lineHeight:1.4,
            background:"rgba(139,47,201,0.06)",
            padding:"6px 14px", borderRadius:8, display:"inline-block",
          }}>
            {email}
          </div>
        </div>

        {/* Options */}
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
          <button className="reply-option-btn" onClick={() => handleOption("gmail")}>
            <GmailIcon />
            <span>Gmail Web</span>
          </button>
          <button className="reply-option-btn" onClick={() => handleOption("outlook")}>
            <OutlookIcon />
            <span>Outlook Web</span>
          </button>
          <button className="reply-option-btn" onClick={() => handleOption("default")}>
            <span style={{ color:C_TEXT_MUTED }}><ExternalIcon /></span>
            <span>Default App</span>
          </button>
        </div>

        {/* Cancel */}
        <div style={{ textAlign:"center" }}>
          <button
            onClick={onClose}
            style={{
              background:"none", border:"none",
              color:C_TEXT_MUTED, cursor:"pointer",
              fontSize:14, fontWeight:600,
              padding:"6px 20px", borderRadius:50,
              transition:"color 0.2s",
            }}
            onMouseEnter={(e) => e.target.style.color = C_TEXT_MAIN}
            onMouseLeave={(e) => e.target.style.color = C_TEXT_MUTED}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════
const GetInTouch = () => {
  const dispatch = useDispatch();
  const { inquiries, loading } = useSelector((state) => state.getInTouch);

  // Reply modal state
  const [replyTarget, setReplyTarget] = useState(null); // holds the record

  useEffect(() => {
    dispatch(fetchInquiries());
  }, [dispatch]);

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteInquiry(id)).unwrap();
      message.success('Enquiry deleted successfully');
      dispatch(fetchInquiries());
    } catch (err) {
      console.error(err);
      message.error('Failed to delete enquiry');
    }
  };

  const columns = [
    {
      title: 'Sender Details',
      key: 'sender',
      render: (_, record) => (
        <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
          <div className="avatar-wrap"><UserIcon /></div>
          <div>
            <div style={{ fontWeight:800, color:C_TEXT_MAIN, fontSize:14.5, marginBottom:4 }}>
              {record.name || "Anonymous"}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              <span style={{ display:"flex", alignItems:"center", gap:6, color:C_TEXT_MUTED, fontSize:12.5 }}>
                <span style={{ color:C_PURPLE }}><MailIcon /></span> {record.email || "No email"}
              </span>
              <span style={{ display:"flex", alignItems:"center", gap:6, color:C_TEXT_MUTED, fontSize:12.5 }}>
                <span style={{ color:C_PINK }}><PhoneIcon /></span> {record.mobile || "No phone"}
              </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Message / Concerns',
      dataIndex: 'inquiryConcerns',
      key: 'inquiryConcerns',
      render: (text) => (
        <div className="message-box">
          {text || <span style={{ color:'#cbd5e1', fontStyle:'italic' }}>No message provided.</span>}
        </div>
      ),
    },
    {
      title: 'Date Received',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      render: (date) => (
        <div style={{ display:"flex", alignItems:"center", gap:6, color:C_TEXT_MAIN, fontSize:13, fontWeight:600 }}>
          <span style={{ color:C_PURPLE }}><CalendarIcon /></span>
          {date ? dayjs(date).format('MMM D, YYYY - h:mm A') : "Unknown"}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 110,
      render: (_, record) => (
        <Space size={8}>
          {/* ── Reply Button ── */}
          <button
            className="action-btn reply-btn"
            title={`Reply to ${record.email}`}
            onClick={() => setReplyTarget(record)}
          >
            <ReplyIcon />
          </button>

          {/* ── Delete Button ── */}
          <Popconfirm
            title={<span style={{ fontWeight:700, color:C_TEXT_MAIN }}>Delete Enquiry?</span>}
            description="Are you sure you want to permanently delete this message?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes, delete"
            cancelText="Cancel"
            okButtonProps={{ danger:true, style:{ borderRadius:8, fontWeight:600 } }}
            cancelButtonProps={{ style:{ borderRadius:8, fontWeight:600 } }}
          >
            <button className="action-btn delete-btn" title="Delete Enquiry">
              <TrashIcon />
            </button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const sortedData = [...(inquiries || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <App>
      <div className="lc">
        <div className="mw">

          {/* ── Header ── */}
          <div className="header-flex">
            <div>
              <SectionLabel>Inbox</SectionLabel>
              <h1 className="sec-title">Website <span style={{ color:C_PINK }}>Enquiries</span></h1>
              <p className="sec-sub">Review and manage contact submissions and questions from delegates.</p>
            </div>
          </div>

          {/* ── Table ── */}
          <div className="glass">
            <Table
              columns={columns}
              dataSource={sortedData}
              rowKey="_id"
              loading={loading}
              pagination={{ pageSize:10, position:["bottomCenter"], showSizeChanger:true }}
              className="premium-table"
            />
          </div>

        </div>
      </div>

      {/* ── Reply Modal (rendered outside table for correct z-index) ── */}
      {replyTarget && (
        <ReplyModal
          visible={!!replyTarget}
          record={replyTarget}
          onClose={() => setReplyTarget(null)}
        />
      )}

      {/* ── Styles ── */}
      <style>{`
        * { box-sizing: border-box; }

        .lc { font-family:'Segoe UI',sans-serif; background:${C_SOFT_BG}; min-height:100vh; padding:40px 0 80px; }
        .mw { max-width:1200px; margin:0 auto; padding:0 24px; }

        .header-flex { display:flex; align-items:flex-end; justify-content:space-between; margin-bottom:40px; gap:20px; flex-wrap:wrap; }
        .sec-title { font-size:clamp(28px,4vw,38px); font-weight:800; color:${C_TEXT_MAIN}; letter-spacing:-0.02em; line-height:1.15; margin:0 0 8px; }
        .sec-sub { font-size:15px; color:${C_TEXT_MUTED}; margin:0; }

        .avatar-wrap { width:42px; height:42px; border-radius:12px; background:linear-gradient(135deg,rgba(139,47,201,0.1),rgba(240,77,160,0.1)); border:1px solid rgba(139,47,201,0.15); color:${C_PURPLE}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

        .message-box { background:#faf8fc; border:1px solid rgba(139,47,201,0.08); padding:12px 16px; border-radius:10px; font-size:13.5px; color:${C_TEXT_MUTED}; line-height:1.5; max-width:400px; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }

        /* Action Buttons */
        .action-btn { width:36px; height:36px; border-radius:10px; border:none; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s; }
        .reply-btn  { background:rgba(16,163,127,0.08); color:#10a37f; }
        .reply-btn:hover  { background:rgba(16,163,127,0.18); transform:translateY(-2px); }
        .delete-btn { background:rgba(248,113,113,0.1);  color:#ef4444; }
        .delete-btn:hover { background:rgba(248,113,113,0.2); transform:translateY(-2px); }

        .glass { background:#fff; border-radius:20px; padding:12px 24px 24px; border:1px solid rgba(139,47,201,0.08); box-shadow:0 10px 40px rgba(0,0,0,0.04); overflow:hidden; }

        /* Ant table */
        .premium-table .ant-table { background:transparent !important; font-family:inherit; }
        .premium-table .ant-table-thead > tr > th { background:transparent !important; color:${C_TEXT_MUTED}; font-weight:700; font-size:12.5px; text-transform:uppercase; letter-spacing:0.05em; border-bottom:2px solid rgba(139,47,201,0.08); padding:20px 16px; }
        .premium-table .ant-table-thead > tr > th::before { display:none !important; }
        .premium-table .ant-table-tbody > tr > td { border-bottom:1px solid rgba(139,47,201,0.05); padding:20px 16px; transition:background 0.3s; vertical-align:top; }
        .premium-table .ant-table-tbody > tr:hover > td { background:#fcfaff !important; }
        .premium-table .ant-pagination { margin-top:24px !important; }
        .premium-table .ant-pagination-item-active { border-color:${C_PINK}; background:rgba(240,77,160,0.05); }
        .premium-table .ant-pagination-item-active a { color:${C_PINK} !important; }

        /* Reply modal option buttons */
        .reply-option-btn { width:100%; padding:14px 18px; border-radius:12px; border:1px solid #ede8f5; background:#fff; font-size:14px; font-weight:600; color:${C_TEXT_MAIN}; cursor:pointer; display:flex; align-items:center; gap:12px; transition:all 0.18s; text-align:left; }
        .reply-option-btn:hover { background:#faf5ff; border-color:rgba(139,47,201,0.25); transform:translateX(4px); }

        /* Animations */
        @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
        @keyframes slideUp { from { opacity:0; transform:translate(-50%,-46%) scale(0.96); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }

        @media(max-width:768px) {
          .glass { padding:12px; border-radius:16px; }
          .header-flex { flex-direction:column; align-items:flex-start; gap:16px; }
        }
      `}</style>
    </App>
  );
};

export default GetInTouch;
